import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Check } from 'lucide-react';
import { getWordTranslation } from '../../utils/localizedContent';
import { displayCategoryLabel } from '../../utils/categoryLabels';
import './RevisionStatsModal.css';

import { Picto } from '../common/Picto';
interface RevisionWordInfo {
  word: string;
  category: string;
  isCorrect: boolean;
  timestamp: number;
  nextReview: number;
  interval?: number;
  easeFactor?: number;
  translation?: string;
  audio?: string;
  isSentence?: boolean;
  grammarType?: 'rule' | 'conjugation' | 'vocabulary';
  subCategory?: string;
}

interface ExtendedRevisionWordInfo extends RevisionWordInfo {
  isFromHistory?: boolean;
  isAwaitingFirstRevision?: boolean;
}

interface GrammarProgress {
  subcategory: string;
  masteredWords: {
    word: string;
    data: { translation: string; explanation?: string; example?: string; }
  }[];
  date: number;
}

interface RevisionStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  wordDataMap: Record<string, Record<string, any>>;
  revisionHistory: RevisionWordInfo[];
  wordProgress?: Record<string, any>;
  languageCode?: string;
  isMobileView?: boolean;
  wordsToReview?: Set<string>;
}

const RevisionStatsModal: React.FC<RevisionStatsModalProps> = ({
  isOpen,
  onClose,
  wordDataMap,
  revisionHistory,
  wordProgress,
  languageCode = 'wf',
  isMobileView = false,
  wordsToReview,
}) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();

  const groupedWords = useMemo(() => {
    const latestWords = new Map<string, ExtendedRevisionWordInfo>();

    if (revisionHistory?.length) {
      revisionHistory.forEach(word => {
        const key = `${word.word}-${word.category}`;
        const existing = latestWords.get(key);
        if (!existing || existing.timestamp < word.timestamp) {
          latestWords.set(key, { ...word, isFromHistory: true });
        }
      });
    }

    try {
      const saved = localStorage.getItem(`grammar-progress-${languageCode}`);
      if (saved) {
        (JSON.parse(saved) as GrammarProgress[]).forEach(cp => {
          cp.masteredWords.forEach(wd => {
            const key = `${wd.word}-Grammaire`;
            if (!latestWords.has(key)) {
              latestWords.set(key, {
                word: wd.word, category: 'Grammaire', isCorrect: true,
                timestamp: cp.date || Date.now(), nextReview: Date.now(),
                interval: 0, easeFactor: 2.5, translation: wd.data.translation,
                grammarType: 'rule', subCategory: cp.subcategory,
                isFromHistory: false, isAwaitingFirstRevision: true,
              });
            }
          });
        });
      }
    } catch {}

    if (wordsToReview?.size) {
      wordsToReview.forEach(key => {
        if (key.startsWith('grammar:')) return;
        const parts = key.split(':');
        if (parts.length < 3) return;
        const category = parts[1];
        const word = parts.slice(2).join(':');
        const histKey = `${word}-${category}`;
        if (latestWords.has(histKey)) return;
        const categoryData = wordDataMap[category] || {};
        const entry = Object.entries(categoryData).find(([k]) =>
          !k.includes('_') && (k === word || k.split('(')[0].trim() === word)
        );
        latestWords.set(histKey, {
          word, category, isCorrect: true,
          timestamp: Date.now(), nextReview: Date.now(),
          interval: 0, easeFactor: 2.5,
          translation: entry ? (entry[1] as any)?.translation ?? '' : '',
          isFromHistory: false, isAwaitingFirstRevision: true,
        });
      });
    }

    const grouped = Array.from(latestWords.values()).reduce<Record<string, ExtendedRevisionWordInfo[]>>((acc, w) => {
      if (!acc[w.category]) acc[w.category] = [];
      acc[w.category].push(w);
      return acc;
    }, {});

    Object.keys(grouped).forEach(cat => {
      grouped[cat].sort((a, b) => {
        const aA = a.isAwaitingFirstRevision || false;
        const bA = b.isAwaitingFirstRevision || false;
        if (aA !== bA) return aA ? -1 : 1;
        return b.timestamp - a.timestamp;
      });
    });

    return grouped;
  }, [revisionHistory, languageCode, wordsToReview, wordDataMap]);

  const categories = Object.keys(groupedWords);

  const getNextReviewTime = useCallback((nextReview: number, wordItem?: any): string => {
    if (wordItem?.isAwaitingFirstRevision) return t('revisionStats.firstRevision');
    if (!nextReview) return t('revisionStats.notScheduled');
    const now = Date.now();
    if (nextReview <= now) return t('revisionStats.available');
    const diff = nextReview - now;
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (days > 0) return t('revisionStats.inDays', { count: days });
    if (hours > 0) return mins > 0 ? t('revisionStats.inHoursMinutes', { hours, mins }) : t('revisionStats.inHours', { hours });
    return mins > 0 ? t('revisionStats.inMinutes', { count: mins }) : t('revisionStats.soon');
  }, [t]);

  const getNextClass = (wordItem: ExtendedRevisionWordInfo) => {
    if (wordItem.isAwaitingFirstRevision) return 'rsm-next--first';
    if (wordItem.nextReview <= Date.now()) return 'rsm-next--avail';
    return 'rsm-next--pending';
  };

  const getStatusClass = (wordItem: ExtendedRevisionWordInfo) => {
    if (wordItem.isAwaitingFirstRevision) return 'rsm-status--first';
    if (wordItem.nextReview <= Date.now()) return 'rsm-status--avail';
    if (wordItem.isCorrect) return 'rsm-status--mastered';
    return 'rsm-status--needs';
  };

  const getStatusIcon = (wordItem: ExtendedRevisionWordInfo) => {
    if (wordItem.isAwaitingFirstRevision) return <Picto name="nouveau" />;
    if (wordItem.nextReview <= Date.now()) return <Picto name="recommencer" />;
    if (wordItem.isCorrect) return <Check size={13} />;
    return <Picto name="attention" />;
  };

  const handleClose = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const renderWordList = (words: ExtendedRevisionWordInfo[], catName: string) => (
    <div className="rsm-word-list">
      {words.map((w, i) => {
        const wd = wordDataMap[catName]?.[w.word];
        const translation = wd ? getWordTranslation(wd, i18n.language) : w.translation;
        return (
          <motion.div
            key={`${w.word}-${w.timestamp}-${i}`}
            className="rsm-word-row"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.03 }}
          >
            <div className={`rsm-status ${getStatusClass(w)}`}>
              {getStatusIcon(w)}
            </div>
            <div className="rsm-word-info">
              <span className="rsm-word-info__word">{w.word}</span>
              {translation && <span className="rsm-word-info__trans">{translation}</span>}
            </div>
            <span className={`rsm-next ${getNextClass(w)}`}>
              {getNextReviewTime(w.nextReview, w)}
            </span>
          </motion.div>
        );
      })}
    </div>
  );

  const renderCategory = (catName: string) => (
    <div className="rsm-cat-section" key={catName}>
      <h3 className="rsm-cat-title">
        <span className="rsm-cat-title__bar" />
        {displayCategoryLabel(catName, t)}
        <span className="rsm-cat-title__count">{t('revisionStats.wordsCount', { count: groupedWords[catName]?.length || 0 })}</span>
      </h3>
      {renderWordList(groupedWords[catName] || [], catName)}
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="rsm-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            className="rsm-modal"
            data-theme={theme}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="rsm-header">
              <h2 className="rsm-header__title">{t('revisionStats.title')}</h2>
              <button className="rsm-header__close" onClick={handleClose} aria-label={t('revisionStats.close') as string}>
                <X size={18} />
              </button>
            </div>

            {/* Category filter */}
            <div className="rsm-filter">
              <div className="rsm-filter__select-wrap">
                <select
                  className="rsm-filter__select"
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                >
                  <option value="all">{t('revisionStats.allCategories', { count: categories.length })}</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {displayCategoryLabel(cat, t)} {t('revisionStats.wordsCount', { count: groupedWords[cat]?.length || 0 })}
                    </option>
                  ))}
                </select>
                <ChevronDown className="rsm-filter__icon" size={14} />
              </div>
            </div>

            {/* Content */}
            <div className="rsm-content">
              {categories.length === 0 ? (
                <div className="rsm-empty">
                  <div className="rsm-empty__icon"><Picto name="livre" /></div>
                  <h3 className="rsm-empty__title">{t('revisionStats.noHistory')}</h3>
                  <p className="rsm-empty__text">{t('revisionStats.noHistoryHint')}</p>
                </div>
              ) : selectedCategory === 'all' ? (
                <div className="rsm-categories">
                  {categories.map(cat => renderCategory(cat))}
                </div>
              ) : groupedWords[selectedCategory]?.length ? (
                renderCategory(selectedCategory)
              ) : (
                <div className="rsm-empty">
                  <div className="rsm-empty__icon"><Picto name="livre" /></div>
                  <h3 className="rsm-empty__title">{t('revisionStats.noWordsInCategory')}</h3>
                  <p className="rsm-empty__text">{t('revisionStats.noWordsInCategoryHint')}</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RevisionStatsModal;
